/**
 * Data processing and rendering functions for each type of chart
 */

import { line, area, curveBasis, curveCardinal } from 'd3-shape'
import { scaleLinear, scaleSymlog, scaleBand } from 'd3-scale'
import { select, mouse } from 'd3-selection'
import { bisector } from 'd3-array'
import { timeFormat } from 'd3-time-format'
import { format } from 'd3-format'
import { interpolate } from 'd3-interpolate'

const timeFormatter = timeFormat( '%B %d, %Y' )
const formatComma = format( ',' )

/**
 * Line graph
 * Cumulative day over day total cases / deaths / recovered
 */
const cumulative = {
	data: null,
	dataType: null,
	showTests: false,
	bisector: null,
	yScale: null,
	yScaleTicks: null,
	yScaleValues: null,
	bounds: {
		minY: 0,
		maxY: 0,
		minX: 0,
		maxX: 0
	},

	init( data, dataType, showTests, logScale ) {
		if ( null === this.data ) {
			let minY   = Infinity
			let maxY   = 0

			// set dataType to first item in array
			this.dataType = dataType[0]

			// process data
			this.data = data.map( ( item, index ) => {
				const value = parseInt( item[ dataType ].replace(/[\*]*/, ''), 10 )
				const tests = parseInt( item['tests'], 10 )
				const date = new Date( item.date )

				minY = Math.min( minY, value )
				maxY = Math.max( maxY, value )

				return {
					'date': date,
					'value': value,
					'tests': tests
				}
			})

			// initialize bounds
			this.bounds.minY = minY
			this.bounds.maxY = maxY
			this.bounds.minX = this.data[0].date
			this.bounds.maxX = this.data[ this.data.length - 1 ].date

			// initialize bisector
			this.bisector = bisector( ( item ) => item.date ).left

			// initialize y scale
			this.yScale = scaleLinear()

			// y scal tick count
			this.yScaleTicks = 4

			if ( logScale ) {
				this.bounds.minY = 0 === minY
				? 0 : Math.pow( 10, Math.floor( minY ).toString().length - 1 )
				this.bounds.maxY = Math.pow( 10, Math.floor( maxY ).toString().length )

				// construct logarithmic scale
				this.yScale = scaleSymlog()

				// y scale values
				this.yScaleValues = []
				for ( let i = this.bounds.minY.toString().length - 1; i < this.bounds.maxY.toString().length; i++ ) {
					const value = 0 === i ? 0 : Math.pow( 10, i )
					this.yScaleValues.push( value )
				}

				// y scal tick count
				this.yScaleTicks = this.yScaleValues.length
			}

			// show tests setting
			this.showTests = showTests
		}
	},

	render( frame ) {
		// Remove existing chart content
		frame.chart.svg.selectAll( '.c-chart__content' )
			.remove()

		// Draw line
		let path = line()
			.x( ( item ) => frame.chart.xScale( item.date ) )
			.y( ( item ) => this.yScale( item.value ) )

		frame.chart.elem.append( 'path' )
			.attr( 'class', 'c-chart__line c-chart__content' )
			.attr( 'fill', 'none' )
			.attr( 'stroke', frame.colors[ this.dataType ] )
			.attr( 'stroke-width', 2 )
			.attr( 'd', path( this.data ) )

		// Tooltip logic
		const focus = frame.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__focus c-chart__content' )
			.style( 'display', 'none' )

		focus.append( 'line' )
			.attr( 'stroke', frame.colors[ this.dataType ] )
			.attr( 'stroke-width', 2 )
			.attr( 'stroke-dasharray', 4 )
			.attr( 'x1', 0 )
			.attr( 'y1', frame.margin.top )
			.attr( 'x2', 0 )
			.attr( 'y2', frame.margin.top + frame.chart.height - 10 )

		focus.append( 'circle' )
			.attr( 'r', 5 )
			.attr( 'fill', '#FFF' )
			.attr( 'stroke', frame.colors[ this.dataType ] )
			.attr( 'stroke-width', 2 )

		 frame.chart.elem.append( 'rect' )
		 	.attr( 'class', 'c-chart__overlay c-chart__content' )
		 	.attr( 'width', frame.chart.width )
		 	.attr( 'height', frame.chart.height )
		 	.attr( 'fill', '#fff' )
		 	.on( 'mousemove', function( item ) {
		 		// intersection logic
		 		const xPos = mouse( this )[0]
		 		const xVal = frame.chart.xScale.invert( xPos )
		 		const i = cumulative.bisector( cumulative.data, xVal, 1 )
		 		const itemPrev = cumulative.data[ i - 1 ]
		 		const itemNext = i < cumulative.data.length ? cumulative.data[ i ] : itemPrev
		 		const itemSelected = xVal - itemPrev.date > itemNext.date - xVal ? itemNext : itemPrev

		 		// tooltip variables
				const chartLeftOffset = frame.$chart.getBoundingClientRect().left
				const chartTopOffset = frame.$chart.getBoundingClientRect().top
				const itemLeftOffset = frame.chart.xScale( itemSelected.date )
				const itemTopOffset = cumulative.yScale( itemSelected.value )
				const leftOffset = chartLeftOffset + itemLeftOffset
				const topOffset = chartTopOffset + itemTopOffset
				const documentWidth = document.documentElement.clientWidth

				let testingInfo
				if ( false === cumulative.showTests ) {
					testingInfo = ''
				} else if ( isNaN( itemSelected.tests ) || 0 === itemSelected.tests ) {
					testingInfo = '<br>(no testing data)'
				} else {
					testingInfo = '<br>out of <strong> ' + formatComma( itemSelected.tests ) + '</strong> tests'
				}

		 		focus.style( 'display', 'inline-block' )
		 			.attr( 'transform', `translate(${frame.chart.xScale( itemSelected.date )}, 0)`)

		 		focus.select( 'circle' )
		 			.attr( 'transform', `translate( 0, ${cumulative.yScale( itemSelected.value )})`)

		 		frame.chart.tooltip
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'hidden' )
					.html( timeFormatter( itemSelected.date ) + '<br><strong>' + formatComma( itemSelected.value ) + '</strong> total ' + frame.dataTypes[ this.dataType ] + testingInfo )

				const tipWidth = frame.$tip.offsetWidth
				const tipHeight = frame.$tip.offsetHeight
				let tipLeftOffset = Math.max( leftOffset - tipWidth, 0 )
				let tipTopOffset = Math.max( topOffset - ( tipHeight * 1.5 ), chartTopOffset )

				frame.chart.tooltip
					.style( 'left', tipLeftOffset + 'px' )
					.style( 'top', tipTopOffset + 'px' )
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'visible' )
			})
			.on( 'mouseout', ( item ) => {
				focus.style( 'display', 'none' )
				frame.chart.tooltip.style( 'display', 'none' )
			})
	}
}

/**
 * Histogram graph
 * Day over day new cases / deaths / recovered
 */
const histogram = {
	data: null,
	showTests: false,
	bandScale: null,
	yScale: null,
	yScaleTicks: 4,
	yScaleValues: null,
	bounds: {
		minY: 0,
		maxY: 0,
		minX: 0,
		maxX: 0
	},

	init( data, dataType, showTests ) {
		if ( null === this.data ) {
			let maxY = 0

			// process data for each data type
			this.data = []
			dataType.forEach( ( type ) => {
				this.data[ type ] = this.processData( data, type )
				let maxVal = Math.max( ...this.data[ type ].map( item => item.average ) )
				maxY = Math.max( maxY, maxVal )
			})

			// initialize bounds
			this.bounds.minY = 0
			this.bounds.maxY = maxY
			this.bounds.minX = new Date( this.data[ dataType[0] ][0].date )
			this.bounds.maxX = new Date( this.data[ dataType[0] ][ this.data[ dataType[0] ].length - 1 ].date )

			// initialize histogram scale
			this.bandScale = scaleBand()

			// initialize y scale
			this.yScale = scaleLinear()

			// show tests setting
			this.showTests = showTests
		}
	},

	processData( data, dataType ) {
		let week = []
		let streak = 0

		// smooth data (Esri doesn't break apart large data dumps
		// so we will average a large dump over the past several
		// days of null data to avoid distorting spikes)
		data.forEach( ( item, index ) => {
			if ( index > 0 ) {
				const current = parseInt( item[ dataType ].replace(/[\*]*/, ''), 10 )
				const prev = parseInt( data[index - 1][ dataType ].replace(/[\*]*/, ''), 10 )
				let diff = ( 'hosp' === dataType || 'icu' === dataType ) ? current : current - prev
				
				if ( 0 === diff ) {
					streak++
				} else if ( streak > 0 && streak <= 5) { // > 5 streak are assumed to not be reporting gaps
					streak++
					const delta = diff / streak

					for ( let i = 1; i < streak; i++ ) {
						const value = parseInt( data[ index - i ][ dataType ].replace(/[\*]*/, ''), 10 )
						data[ index - i ][ dataType ] = String( value + ( delta * ( streak - i ) ) )
					}

					streak = 0
				}
			}
		})

		// process data
		return data.map( ( item, index ) => {
			if ( index > 0 ) {
				// total
				const current = parseInt( item[ dataType ].replace(/[\*]*/, ''), 10 )
				const prev = parseInt( data[index - 1][ dataType ].replace(/[\*]*/, ''), 10 )
				let diff = ( 'hosp' === dataType || 'icu' === dataType ) ? current : current - prev

				// fix for massive recovery spike on July 17th
				// we need to reduce the height or else if messes up the graph
				let maxed = false
				if ( 'recov' === dataType && '7/17/2020' === item['date'] ) {
					diff = Math.min( diff, 3500 )
					maxed = true
				}

				// 7-day average
				const weekLength = week.unshift( diff )
				if ( weekLength > 7 ) {
					week.pop()
				}
				const weekSum = week.reduce( ( a, b ) => a + b, 0 )
				const weekAvg = Math.round( weekSum / week.length ) || 0

				return {
					'date': item['date'],
					'dateFormatted': new Date( item['date'] ),
					'value': diff,
					'average': weekAvg,
					'maxed': maxed,
				}
			}
		}).slice(1)
	},

	render( frame, dataType, secondaryLayer = false ) {
		// Histogram bars
		this.bandScale.range([0, frame.chart.width])
			.domain( this.data[ dataType ].map( ( item ) => item.date ) )
			.paddingInner( 0 )

		// Remove existing chart content
		if ( false === secondaryLayer ) {
			frame.chart.elem.selectAll( '.c-chart__content' )
			.remove()
		}

		// Draw backing area
		let areaPath = area()
			.x( ( item ) => frame.chart.xScale( item.dateFormatted ) )
			.y0( frame.chart.height )
			.y1( ( item ) => this.yScale( item.average ) )
			.curve( curveBasis )

		// We need a white backing for additional chart layers
		if ( false !== secondaryLayer ) {
			frame.chart.elem.append( 'path' )
				.attr( 'class', 'c-chart__area c-chart__area--backing c-chart__content' )
				.attr( 'fill', '#FFF' )
				.attr( 'd', areaPath( this.data[ dataType ] ) )	
		}

		// Draw area fill
		frame.chart.elem.append( 'path' )
			.attr( 'class', `c-chart__area c-chart__area--${dataType} c-chart__content` )
			.attr( 'fill', frame.colors[ dataType ] )
			.attr( 'd', areaPath( this.data[ dataType ] ) )

		// Add histogram bars
		frame.chart.elem.selectAll()
			.data( this.data[ dataType ] )
			.enter()
				.append( 'rect' )
				.attr( 'class', 'c-chart__bar c-chart__content' )
				.attr( 'x', ( item ) => this.bandScale( item.date ) )
				.attr( 'y', ( item ) => this.yScale( item.average ) )
				.attr( 'height', ( item ) => frame.chart.height - this.yScale( item.average ) )
				.attr( 'width', this.bandScale.bandwidth() )
				.attr( 'fill', 'transparent' )
			.on( 'mouseover', function ( item ) {
				const currentBar = select( this )
				const chartLeftOffset = frame.$chart.getBoundingClientRect().left
				const chartTopOffset = frame.$chart.getBoundingClientRect().top
				const barLeftOffset = parseInt( currentBar.attr( 'x' ), 0 )
				const barTopOffset = parseInt( currentBar.attr( 'y' ), 0 )
				const barWidth = parseInt( currentBar.attr( 'width' ), 0 )
				const leftOffset = chartLeftOffset + barLeftOffset
				const topOffset = chartTopOffset + barTopOffset
				const documentWidth = document.documentElement.clientWidth

				// flag July 17th recoveries as maxed out
				let maxed_flag = true === item.maxed ? '+' : ''

				// plot total hospitalizations as opposed to new
				let new_flag = ( 'hosp' == dataType || 'icu' == dataType ) ? 'total' : 'new'

				frame.chart.tooltip
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'hidden' )
					.html( timeFormatter( new Date( item.date ) ) + '<br><strong>' + 
						formatComma( item.average.toFixed(1) ) + maxed_flag + '</strong> ' + 
						new_flag + ' ' + frame.dataTypes[ dataType ] + 
						'<br>on average per day over the' +
						'<br>past 7 days' )

				const tipWidth = frame.$tip.offsetWidth
				const tipHeight = frame.$tip.offsetHeight
				let tipLeftOffset = barWidth + leftOffset - ( tipWidth / 2 )
				let tipTopOffset = topOffset - tipHeight

				if ( tipLeftOffset + tipWidth > documentWidth ) {
					tipLeftOffset = documentWidth - tipWidth
				} else if ( tipLeftOffset < 0 ) {
					tipLeftOffset = 0
				}
				
				frame.chart.tooltip
					.style( 'left', tipLeftOffset + 'px' )
					.style( 'top', tipTopOffset + 'px' )
					.style( 'visibility', 'visible' )

				currentBar.node().classList.add( 'c-chart__bar--hovered' )
				
				// toggle area chart hover states
				document.querySelector( `.c-chart` ).classList.add( 'c-chart--hovered' )
				document.querySelector( `.c-chart__area--${dataType}` ).classList.add( 'c-chart__area--hovered' )
				document.querySelector( `.c-chart__line--${dataType}` ).classList.add( 'c-chart__line--hovered' )
			})
			.on( 'mouseout', function ( item ) {
				frame.chart.tooltip.style( 'display', 'none' )
				select( this ).node().classList.remove( 'c-chart__bar--hovered' )

				// toggle area chart hover states
				document.querySelector( `.c-chart` ).classList.remove( 'c-chart--hovered' )
				document.querySelector( `.c-chart__area--${dataType}` ).classList.remove( 'c-chart__area--hovered' )
				document.querySelector( `.c-chart__line--${dataType}` ).classList.remove( 'c-chart__line--hovered' )
			})

		// Draw average trend line
		let path = line()
			.x( ( item ) => frame.chart.xScale( item.dateFormatted ) )
			.y( ( item ) => this.yScale( item.average ) )
			.curve( curveBasis )

		frame.chart.elem.append( 'path' )
			.attr( 'class', `c-chart__line c-chart__line--trend c-chart__line--${dataType} c-chart__content` )
			.attr( 'fill', 'none' )
			.attr( 'stroke', frame.colors[ dataType ] )
			.attr( 'stroke-width', 2 )
			.attr( 'pointer-events', 'none' )
			.attr( 'd', path( this.data[ dataType ] ) )
	}
}

export {
	cumulative,
	histogram
}
